import dayjs from 'dayjs'
import React, { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { DateInput } from 'semantic-ui-calendar-react'
import { Form, Icon, LabelDetail, Message, Tab } from 'semantic-ui-react'
import { ThemeContext } from 'styled-components'
import { FarmersQuery } from '~/QueryHooks/Farmers'
import { FarmersService } from '~/api/services/farmers/index'
import CustomInput from '~/components/CustomInput'
import Dialog from '~/components/Dialog'
import notify from '~/components/Notify'
import { StyledTabPane } from '~/pages/Dairy/Settings/General/style'
import { useAuth } from '~/routes/components/AuthProvider'
import { formatNumber, getLastThreeDaysOfMonth, handleErrors } from '~/utils/helpers'
import { StyledDropdown, StyledFormGroup, StyledLabel } from './style'
import { StyledMenuItem } from '~/pages/Dairy/CloudStorage/style'
interface ArticleBookingFormProps {
  handleClose: () => void
  handleSubmit: () => void
  startBookingDate: string
  openModal: boolean
  farmer: string
  farmerData: any
}
const defaultArticleValues = {
  id: '',
  custom: 0,
  netPrice: 0,
  dateFrom: '',
  dateTo: '',
  languages: [],
  amount: '',
  interval: '',
  percent: '',
  invoiceType: ''
}

const defaultBookingData = {
  dateFrom: '',
  dateTo: '',
  languages: [],
  amount: '',
  netPrice: '0'
}

const intervals = ['monthly', 'quarterly', 'half_yearly', 'annually']
const ADDITION_INVOICE_TYPE = 'additional'

const ArticleBookingForm: React.FC<ArticleBookingFormProps> = (props) => {
  const { handleClose, handleSubmit, startBookingDate, openModal, farmerData } = props
  const { t } = useTranslation()
  const { id } = useParams()
  const { firstColor } = useContext(ThemeContext)
  const { systemData: { languages, logged: { languageId } } } = useAuth()
  const languageLocal = languages.find(language => language.id === languageId)
  const [isBusy, setIsBusy] = useState(false)
  const [open, setOpen] = useState(Boolean(openModal))
  const [article, setArticle] = useState(defaultArticleValues)
  const [bookingData, setBookingData] = useState(defaultBookingData)
  const [prices, setPrices] = useState({
    netTotal: '0',
    tax: '0',
    grossTotal: '0'
  })

  const {
    data: articleList,
    isLoading: isLoadingList
  } = FarmersQuery.getArticleList()

  useEffect(() => {
    setOpen(openModal)
  }, [openModal])

  const customArticle = Boolean(article?.custom)

  function roundToFixed(number) {
    return isNaN(parseFloat(number.toString().replace(',', '.'))) ? '0.00' : parseFloat(number.toString().replace(',', '.')).toFixed(2)
  }

  useEffect(() => {
    if (article.id) {
      const netPrice = customArticle ? parseFloat(bookingData.netPrice.toString().replace(',', '.')) : article?.netPrice
      const netTotal = isNaN(parseInt(bookingData?.amount)) ? '0.00' : roundToFixed(netPrice * parseInt(bookingData?.amount))
      //@ts-ignore
      const tax = roundToFixed(article?.percent / 100 * netTotal)
      const grossTotal = formatNumber(parseFloat(netTotal) + parseFloat(tax), 2)
      setPrices({
        netTotal,
        tax,
        grossTotal
      })
    }
  }, [bookingData.amount, bookingData.netPrice])

  function handleCloseBookmark() {
    setBookingData(defaultBookingData)
    handleClose()
  }

  const handleKeyPress = (event) => {
    event.preventDefault();
  }

  function parseDate(value) {
    const arrayOfValues = value.split('.')
    return `${arrayOfValues[2]}-${arrayOfValues[1]}-${arrayOfValues[0]}`
  }

  function getDaysAhead(date, disableLastThreeDays) {
    const inputDate = parseDate(date);
    const parsedDate = dayjs(inputDate, { format: 'DD.MM.YYYY' });

    if (parsedDate.isValid()) {
      const nextDay = parsedDate.add(1, 'day');

      // Check if the next day is in the last three days of the month
      const isLastThreeDays = nextDay.date() > nextDay.daysInMonth() - 3;

      // If the next day is in the last three days, skip to the beginning of the next month
      if (isLastThreeDays && disableLastThreeDays) {
        const nextMonth = nextDay.add(1, 'month').startOf('month');
        return nextMonth.format('DD.MM.YYYY');
      }

      return nextDay.format('DD.MM.YYYY');
    }

    return parsedDate.format('DD.MM.YYYY');
  }

  function checkAheadDate(dateFrom, dateTo) {
    const dateFromParsed = parseDate(dateFrom)
    const dateToParsed = parseDate(dateTo)
    return dayjs(dateFromParsed).diff(dayjs(dateToParsed)) > 0
  }

  function getMenuItem(language) {

    return <StyledMenuItem
      key={language?.id}
      className='tab-item'
    >
      <div>{language?.label}</div>
    </StyledMenuItem>
  }

  function renderLanguageFields() {
    return <Tab
      menu={{ secondary: true, pointing: true }}
      renderActiveOnly={false}
      panes={
        languages.map((language) => {
          const indexLanguage = languages.findIndex(languageIndex => languageIndex['id'] === language.id)
          return {
            menuItem: getMenuItem(language),
            pane: <StyledTabPane key={language.id}>
              <Form.Input
                width={8}
                required
                label={t('Name')}
                placeholder={t('Name')}
                value={bookingData.languages[indexLanguage]?.name}
                onChange={(e, data) => {
                  if (data.value.length > 100) {
                    notify(t('Max char. is 100'), 'error')
                    return
                  }
                  const tempLangs = [...bookingData.languages]
                  tempLangs[indexLanguage].name = data.value
                  setBookingData({ ...bookingData, languages: tempLangs })
                }}
              />
            </StyledTabPane>
          }
        })}
    />
  }

  function renderAddBookmarkForm() {
    const lastThreeDaysInMonths = getLastThreeDaysOfMonth()
    const disableLastThreeDays = intervals.find(interval => interval === article?.interval)
    const { dateFrom, dateTo, amount } = bookingData
    const minDateFrom = article?.invoiceType !== ADDITION_INVOICE_TYPE ? dayjs(startBookingDate).format('DD.MM.YYYY') : null

    return <Form>
      <StyledDropdown
        selection
        scrolling
        search
        noResultsMessage={t('No articles found.')}
        selectOnBlur={false}
        label={t('Select article')}
        options={articleList ? articleList?.map(article => ({ value: article.id, text: `${article.name} (${article.uniqueId})` })) : []}
        placeholder={t('Select article')}
        onChange={(e, data) => {
          const article = articleList?.find(article => article?.id === data.value)
          setBookingData({
            ...bookingData,
            netPrice: article?.netPrice || '',
            languages: article?.languages || [],
            dateFrom: '',
            dateTo: ''
          })
          setArticle(article)
        }}
      />
      {Boolean(article.id) && <>
        <StyledFormGroup $custom={customArticle} widths={'equal'}>
          <Form.Field required>
            <label>{t('Booking start date')}</label>
            <DateInput
              name='dateTime'
              onKeyPress={handleKeyPress}
              placeholder={t('Booking start date')}
              clearable
              closable
              disable={disableLastThreeDays ? lastThreeDaysInMonths : []}
              minDate={minDateFrom}
              autoComplete='off'
              markColor={'blue'}
              localization={languageLocal.code}
              closeOnMouseLeave={false}
              dateFormat={'DD.MM.YYYY'}
              className={'datePicker'}
              value={dateFrom}
              iconPosition='left'
              onChange={(event, { name, value }) => {
                const { dateTo } = bookingData
                setBookingData(
                  {
                    ...bookingData,
                    dateFrom: value,
                    dateTo: dateTo !== '' && checkAheadDate(value, dateTo) ? getDaysAhead(value, disableLastThreeDays) : dateTo
                  })
              }}
            />
          </Form.Field>
          {!customArticle && <Form.Field>
            <label>{t('Booking end date')}</label>
            <DateInput
              name='dateTime'
              onKeyPress={handleKeyPress}
              placeholder={t('Booking end date')}
              clearable
              closable
              disabled={dateFrom === ''}
              disable={disableLastThreeDays ? lastThreeDaysInMonths : []}
              minDate={getDaysAhead(dateFrom, disableLastThreeDays)}
              initialDate={getDaysAhead(dateFrom, disableLastThreeDays)}
              autoComplete='off'
              markColor={'blue'}
              localization={languageLocal.code}
              closeOnMouseLeave={false}
              dateFormat={'DD.MM.YYYY'}
              className={'datePicker'}
              value={dateTo}
              iconPosition='left'
              onChange={(event, { name, value }) => setBookingData({ ...bookingData, dateTo: value })}
            />
          </Form.Field>}
        </StyledFormGroup>
        {customArticle && renderLanguageFields()}
        <Form.Group>
          <Form.Field width={8} required>
            <label>{t('Amount')}</label>
            <CustomInput
              value={amount}
              allowDecimals={false}
              onChange={(value) => setBookingData({ ...bookingData, amount: value as string })}
              placeholder={t('Please enter amount')}
            />
          </Form.Field>
          {customArticle && <Form.Field width={8} required>
            <label>{t('Net price (in €)')}</label>
            <CustomInput
              value={bookingData.netPrice}
              decimalScale={5}
              allowDecimals={true}
              placeholder={t('Enter net price')}
              onChange={(value) => setBookingData({ ...bookingData, netPrice: value as string })}
            />
          </Form.Field>}
        </Form.Group>
      </>}
    </Form>
  }

  function checkErrors() {
    const { dateFrom, languages, amount, netPrice } = bookingData
    if (dateFrom === '') {
      return t('Please add booking start date')
    }
    if (customArticle && Boolean(languages.find(lang => !lang.name))) {
      return t('Please enter name for all languages')
    }
    if (!amount) {
      return t('Please enter amount')
    }
    if (customArticle && (!netPrice || parseFloat(netPrice.toString().replace(',', '.')) <= 0)) {
      return t('Please enter net price bigger then 0')
    }
    return false
  }

  async function handleAddBookmark() {
    const error = checkErrors()
    if (error) {
      notify(error, 'error')
      return
    }
    const { dateFrom, dateTo, languages, amount, netPrice } = bookingData
    const articleData = articleList.find(articleItem => articleItem.id === article.id) || {}
    const payload = {
      dateFrom: parseDate(dateFrom),
      dateTo: dateTo ? parseDate(dateTo) : '',
      amount: amount.toString().replace('.', ','),
      articleId: article.id,
      netPrice: customArticle && Boolean(netPrice) ? netPrice.toString().replace(',', '.') : ''
    }
    if (customArticle) {
      payload['languages'] = languages
    }
    setIsBusy(true)
    try {
      await FarmersService.addArticle(id, payload)
      const companyData = `${farmerData.supplierNo} - ${farmerData.companyName}`
      const articleName = `${articleData?.name} (${articleData?.uniqueId})`
      notify(t('The article {{article}} was successfully booked for the farmer {{famerData}}.', { article: articleName, farmerData: companyData }), 'success')
      setBookingData(defaultBookingData)
      handleSubmit()
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false)
  }

  function renderFarmerHeader() {
    return <StyledLabel $firstColor={firstColor}>
      <Icon className='icon-farmer-icon font-size-2-m' />
      <LabelDetail>{t('Farmer: {{farmer}}', { farmer: props.farmer })}</LabelDetail>
    </StyledLabel>
  }

  const { netTotal, tax, grossTotal } = prices

  return <Dialog
    isDialogOpen={open}
    textHeader={t('Add new booking')}
    textSubmit={t('Booking')}
    isLoading={isBusy || isLoadingList}
    textClose={t('Cancel')}
    minHeight={'300px'}
    htmlContent={false}
    textContent={<>
      {renderFarmerHeader()}
      {renderAddBookmarkForm()}
      {Boolean(article.id) && <Message info>
        <p>{t('Net total (in €) = {{netTotal}}', { netTotal: formatNumber(netTotal, 2) })}</p>
        <p>{t('{{percent}} % Tax (in €) = {{tax}}', { percent: formatNumber(article.percent, 2), tax: formatNumber(tax, 2) })}</p>
        <p>{t('Gross total (in €) = {{grossTotal}}', { grossTotal })}</p>
      </Message>
      }
    </>}
    handleSubmit={() => handleAddBookmark()}
    handleClose={handleCloseBookmark}
  />
}
export default ArticleBookingForm